import _classCallCheck from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/createClass.js";
import { SubscriptionManager } from '../utils/subscription-manager.mjs';
import { velocityPerSecond } from '../utils/velocity-per-second.mjs';
import { warnOnce } from '../utils/warn-once.mjs';
import { time } from '../frameloop/sync-time.mjs';
import { frame } from '../frameloop/frame.mjs';

/**
 * Maximum time between the value of two frames, beyond which we
 * assume the velocity has since been 0.
 */
var MAX_VELOCITY_DELTA = 30;
var isFloat = function isFloat(value) {
  return !isNaN(parseFloat(value));
};
var collectMotionValues = {
  current: undefined
};
/**
 * `MotionValue` is used to track the state and velocity of motion values.
 *
 * @public
 */
var MotionValue = /*#__PURE__*/function () {
  /**
   * @param init - The initiating value
   * @param config - Optional configuration options
   *
   * -  `transformer`: A function to transform incoming values with.
   *
   * @internal
   */
  function MotionValue(init) {
    var _this = this;
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    _classCallCheck(this, MotionValue);
    /**
     * This will be replaced by the build step with the latest version number.
     * When MotionValues are provided to motion components, warn if versions are mixed.
     */
    this.version = "11.5.4";
    /**
     * Tracks whether this value can output a velocity. Currently this is only true
     * if the value is numerical, but we might be able to widen the scope here and support
     * other value types.
     *
     * @internal
     */
    this.canTrackVelocity = null;
    /**
     * An object containing a SubscriptionManager for each active event.
     */
    this.events = {};
    this.updateAndNotify = function (v) {
      var render = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      var currentTime = time.now();
      /**
       * If we're updating the value during another frame or eventloop
       * than the previous frame, then the we set the previous frame value
       * to current.
       */
      if (_this.updatedAt !== currentTime) {
        _this.setPrevFrameValue();
      }
      _this.prev = _this.current;
      _this.setCurrent(v);
      // Update update subscribers
      if (_this.current !== _this.prev && _this.events.change) {
        _this.events.change.notify(_this.current);
      }
      // Update render subscribers
      if (render && _this.events.renderRequest) {
        _this.events.renderRequest.notify(_this.current);
      }
    };
    this.hasAnimated = false;
    this.setCurrent(init);
    this.owner = options.owner;
  }
  return _createClass(MotionValue, [{
    key: "setCurrent",
    value: function setCurrent(current) {
      this.current = current;
      this.updatedAt = time.now();
      if (this.canTrackVelocity === null && current !== undefined) {
        this.canTrackVelocity = isFloat(this.current);
      }
    }
  }, {
    key: "setPrevFrameValue",
    value: function setPrevFrameValue() {
      var prevFrameValue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.current;
      this.prevFrameValue = prevFrameValue;
      this.prevUpdatedAt = this.updatedAt;
    }
    /**
     * Adds a function that will be notified when the `MotionValue` is updated.
     *
     * It returns a function that, when called, will cancel the subscription.
     *
     * When calling `onChange` inside a React component, it should be wrapped with the
     * `useEffect` hook. As it returns an unsubscribe function, this should be returned
     * from the `useEffect` function to ensure you don't add duplicate subscribers..
     *
     * ```jsx
     * export const MyComponent = () => {
     *   const x = useMotionValue(0)
     *   const y = useMotionValue(0)
     *   const opacity = useMotionValue(1)
     *
     *   useEffect(() => {
     *     function updateOpacity() {
     *       const maxXY = Math.max(x.get(), y.get())
     *       const newOpacity = transform(maxXY, [0, 100], [1, 0])
     *       opacity.set(newOpacity)
     *     }
     *
     *     const unsubscribeX = x.on("change", updateOpacity)
     *     const unsubscribeY = y.on("change", updateOpacity)
     *
     *     return () => {
     *       unsubscribeX()
     *       unsubscribeY()
     *     }
     *   }, [])
     *
     *   return <motion.div style={{ x }} />
     * }
     * ```
     *
     * @param subscriber - A function that receives the latest value.
     * @returns A function that, when called, will cancel this subscription.
     *
     * @deprecated
     */
  }, {
    key: "onChange",
    value: function onChange(subscription) {
      if (process.env.NODE_ENV !== "production") {
        warnOnce(false, "value.onChange(callback) is deprecated. Switch to value.on(\"change\", callback).");
      }
      return this.on("change", subscription);
    }
  }, {
    key: "on",
    value: function on(eventName, callback) {
      var _this2 = this;
      if (!this.events[eventName]) {
        this.events[eventName] = new SubscriptionManager();
      }
      var unsubscribe = this.events[eventName].add(callback);
      if (eventName === "change") {
        return function () {
          unsubscribe();
          /**
           * If we have no more change listeners by the start
           * of the next frame, stop active animations.
           */
          frame.read(function () {
            if (!_this2.events.change.getSize()) {
              _this2.stop();
            }
          });
        };
      }
      return unsubscribe;
    }
  }, {
    key: "clearListeners",
    value: function clearListeners() {
      for (var eventManagers in this.events) {
        this.events[eventManagers].clear();
      }
    }
    /**
     * Attaches a passive effect to the `MotionValue`.
     *
     * @internal
     */
  }, {
    key: "attach",
    value: function attach(passiveEffect, stopPassiveEffect) {
      this.passiveEffect = passiveEffect;
      this.stopPassiveEffect = stopPassiveEffect;
    }
    /**
     * Sets the state of the `MotionValue`.
     *
     * @remarks
     *
     * ```jsx
     * const x = useMotionValue(0)
     * x.set(10)
     * ```
     *
     * @param latest - Latest value to set.
     * @param render - Whether to notify render subscribers. Defaults to `true`
     *
     * @public
     */
  }, {
    key: "set",
    value: function set(v) {
      var render = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      if (!render || !this.passiveEffect) {
        this.updateAndNotify(v, render);
      } else {
        this.passiveEffect(v, this.updateAndNotify);
      }
    }
  }, {
    key: "setWithVelocity",
    value: function setWithVelocity(prev, current, delta) {
      this.set(current);
      this.prev = undefined;
      this.prevFrameValue = prev;
      this.prevUpdatedAt = this.updatedAt - delta;
    }
    /**
     * Set the state of the `MotionValue`, stopping any active animations,
     * effects, and resets velocity to `0`.
     */
  }, {
    key: "jump",
    value: function jump(v) {
      var endAnimation = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      this.updateAndNotify(v);
      this.prev = v;
      this.prevUpdatedAt = this.prevFrameValue = undefined;
      endAnimation && this.stop();
      if (this.stopPassiveEffect) this.stopPassiveEffect();
    }
    /**
     * Returns the latest state of `MotionValue`
     *
     * @returns - The latest state of `MotionValue`
     *
     * @public
     */
  }, {
    key: "get",
    value: function get() {
      if (collectMotionValues.current) {
        collectMotionValues.current.push(this);
      }
      return this.current;
    }
    /**
     * @public
     */
  }, {
    key: "getPrevious",
    value: function getPrevious() {
      return this.prev;
    }
    /**
     * Returns the latest velocity of `MotionValue`
     *
     * @returns - The latest velocity of `MotionValue`. Returns `0` if the state is non-numerical.
     *
     * @public
     */
  }, {
    key: "getVelocity",
    value: function getVelocity() {
      var currentTime = time.now();
      if (!this.canTrackVelocity || this.prevFrameValue === undefined || currentTime - this.updatedAt > MAX_VELOCITY_DELTA) {
        return 0;
      }
      var delta = Math.min(this.updatedAt - this.prevUpdatedAt, MAX_VELOCITY_DELTA);
      // Casts because of parseFloat's poor typing
      return velocityPerSecond(parseFloat(this.current) - parseFloat(this.prevFrameValue), delta);
    }
    /**
     * Registers a new animation to control this `MotionValue`. Only one
     * animation can drive a `MotionValue` at one time.
     *
     * ```jsx
     * value.start()
     * ```
     *
     * @param animation - A function that starts the provided animation
     *
     * @internal
     */
  }, {
    key: "start",
    value: function start(startAnimation) {
      var _this3 = this;
      this.stop();
      return new Promise(function (resolve) {
        _this3.hasAnimated = true;
        _this3.animation = startAnimation(resolve);
        if (_this3.events.animationStart) {
          _this3.events.animationStart.notify();
        }
      }).then(function () {
        if (_this3.events.animationComplete) {
          _this3.events.animationComplete.notify();
        }
        _this3.clearAnimation();
      });
    }
    /**
     * Stop the currently active animation.
     *
     * @public
     */
  }, {
    key: "stop",
    value: function stop() {
      if (this.animation) {
        this.animation.stop();
        if (this.events.animationCancel) {
          this.events.animationCancel.notify();
        }
      }
      this.clearAnimation();
    }
    /**
     * Returns `true` if this value is currently animating.
     *
     * @public
     */
  }, {
    key: "isAnimating",
    value: function isAnimating() {
      return !!this.animation;
    }
  }, {
    key: "clearAnimation",
    value: function clearAnimation() {
      delete this.animation;
    }
    /**
     * Destroy and clean up subscribers to this `MotionValue`.
     *
     * The `MotionValue` hooks like `useMotionValue` and `useTransform` automatically
     * handle the lifecycle of the returned `MotionValue`, so this method is only necessary if you've manually
     * created a `MotionValue` via the `motionValue` function.
     *
     * @public
     */
  }, {
    key: "destroy",
    value: function destroy() {
      this.clearListeners();
      this.stop();
      if (this.stopPassiveEffect) {
        this.stopPassiveEffect();
      }
    }
  }]);
}();
function motionValue(init, options) {
  return new MotionValue(init, options);
}
export { MotionValue, collectMotionValues, motionValue };